'use client';
import React, { useState } from 'react';
import Image from 'next/image';
import { Heart } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { ProductBase, ProductDetail } from '@/types/product';
import useStrapiHome from '@/hooks/useStrapiHome';
import useLoMasVendidoProducts from '@/hooks/useLoMasVendidoProducts';
import StrapiBlocksRenderer from '@/components/ui/StrapiBlocksRenderer';
import { generateColor } from '@/utils/productMapper';
import { useAddToCart } from '@/hooks/useAddToCart';
import { CartItem } from '@/types/cart';

interface ProductsSectionMobileProps {
  className?: string;
  title?: string;
  subtitle?: string;
  products?: ProductBase[];
}

// Función para convertir hex a rgba
function hexToRgba(hex: string, alpha: number): string {
  if (!hex || typeof hex !== 'string') {
    return 'rgba(255, 222, 59, 0.26)'; // Fallback a amarillo
  }

  hex = hex.replace('#', '');

  if (hex.length !== 6) {
    return 'rgba(255, 222, 59, 0.26)'; // Fallback a amarillo
  }

  const bigint = parseInt(hex, 16);

  if (isNaN(bigint)) {
    return 'rgba(255, 222, 59, 0.26)'; // Fallback a amarillo
  }

  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;

  const validAlpha = Math.max(0, Math.min(1, alpha));

  return `rgba(${r}, ${g}, ${b}, ${validAlpha})`;
}

// Función para formatear precios con coma
function formatPrice(price: number): string {
  return price.toFixed(2).replace('.', ',');
}

const ProductsSectionMobile: React.FC<ProductsSectionMobileProps> = ({
  className = '',
  title = 'LO MÁS VENDIDO',
  subtitle = 'Seguro que te interesa',
  products = [],
}) => {
  const router = useRouter();
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [showAllProducts, setShowAllProducts] = useState(false);
  const { addToCart } = useAddToCart();
  const { data: strapiData, loading: strapiLoading } = useStrapiHome();
  const {
    data: loMasVendidoProducts,
    isLoading: productsLoading,
    error: productsError,
  } = useLoMasVendidoProducts();

  // Verificar si la sección debe ser visible
  const isVisible = strapiData?.loMasVendido?.visible ?? true;

  // No renderizar si no es visible
  if (!isVisible) {
    return null;
  }

  // Usar productos de Strapi/PrestaShop si están disponibles, sino usar los proporcionados
  const productsToUse =
    loMasVendidoProducts && loMasVendidoProducts.length > 0 ? loMasVendidoProducts : products;

  // Extraer título de Strapi si está disponible
  const strapiTitle = strapiData?.loMasVendido?.title;

  // Usar título de Strapi si está disponible, sino usar el título por defecto
  const finalTitle =
    strapiTitle && strapiTitle.length > 0 ? <StrapiBlocksRenderer data-component-id="src/components/sections/mobile/ProductsSectionMobile.tsx:87:44" data-component-path="src/components/sections/mobile/ProductsSectionMobile.tsx" data-component-line="87" data-component-file="ProductsSectionMobile.tsx" data-component-name="StrapiBlocksRenderer" data-component-content="%7B%22elementName%22%3A%22StrapiBlocksRenderer%22%7D" blocks={strapiTitle} /> : title;

  const handleFavoriteToggle = (id: string) => {
    setFavorites((prev) => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(id)) {
        newFavorites.delete(id);
      } else {
        newFavorites.add(id);
      }
      return newFavorites;
    });
  };

  const handleAddToCart = (product: ProductBase) => {
    const cartItem: CartItem = {
      id: product.id.toString(),
      name: product.title,
      image: product.image,
      price: product.priceFinalInc,
      tax: product.priceFinalInc - product.priceFinalExc,
      quantity: 1,
      weight: product.weight || '',
      brand: product.brand || '',
      sku: product.slug,
      barcode: product.barcode || '',
    };
    addToCart(cartItem);
  };

  // Mostrar loading solo para productos, pero mostrar título inmediatamente
  const showLoadingSpinner = productsLoading;

  return (
    <div data-component-id="src/components/sections/mobile/ProductsSectionMobile.tsx:121:4" data-component-path="src/components/sections/mobile/ProductsSectionMobile.tsx" data-component-line="121" data-component-file="ProductsSectionMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`py-2 ${className}`}>
      <div data-component-id="src/components/sections/mobile/ProductsSectionMobile.tsx:122:6" data-component-path="src/components/sections/mobile/ProductsSectionMobile.tsx" data-component-line="122" data-component-file="ProductsSectionMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22px-4%20mb-2%20text-left%22%7D" className="px-4 mb-2 text-left">
        <h2 data-component-id="src/components/sections/mobile/ProductsSectionMobile.tsx:123:8" data-component-path="src/components/sections/mobile/ProductsSectionMobile.tsx" data-component-line="123" data-component-file="ProductsSectionMobile.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22font-tt-norms-extra-black%20text-2xl%20leading-7%20text-global-1%20mb-1%22%7D" className="font-tt-norms-extra-black text-2xl leading-7 text-global-1 mb-1">
          {finalTitle}
        </h2>
        <div data-component-id="src/components/sections/mobile/ProductsSectionMobile.tsx:126:8" data-component-path="src/components/sections/mobile/ProductsSectionMobile.tsx" data-component-line="126" data-component-file="ProductsSectionMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-global-9%20rounded%20px-3%20inline-block%22%7D" className="bg-global-9 rounded px-3 inline-block">
          <span data-component-id="src/components/sections/mobile/ProductsSectionMobile.tsx:127:10" data-component-path="src/components/sections/mobile/ProductsSectionMobile.tsx" data-component-line="127" data-component-file="ProductsSectionMobile.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-tt-norms-medium%20text-sm%20leading-4%20text-global-1%22%7D" className="font-tt-norms-medium text-sm leading-4 text-global-1">{subtitle}</span>
        </div>
      </div>

      {showLoadingSpinner ? (
        <div data-component-id="src/components/sections/mobile/ProductsSectionMobile.tsx:132:8" data-component-path="src/components/sections/mobile/ProductsSectionMobile.tsx" data-component-line="132" data-component-file="ProductsSectionMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20justify-center%20items-center%20h-32%20px-4%22%7D" className="flex justify-center items-center h-32 px-4">
          <div data-component-id="src/components/sections/mobile/ProductsSectionMobile.tsx:133:10" data-component-path="src/components/sections/mobile/ProductsSectionMobile.tsx" data-component-line="133" data-component-file="ProductsSectionMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-spin%20rounded-full%20h-6%20w-6%20border-b-2%20border-orange-500%22%7D" className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500"></div>
          <span data-component-id="src/components/sections/mobile/ProductsSectionMobile.tsx:134:10" data-component-path="src/components/sections/mobile/ProductsSectionMobile.tsx" data-component-line="134" data-component-file="ProductsSectionMobile.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22ml-2%20text-gray-600%20text-sm%22%2C%22textContent%22%3A%22Cargando%20productos...%22%7D" className="ml-2 text-gray-600 text-sm">Cargando productos...</span>
        </div>
      ) : (
        /* Grid de productos 2x2 */
        <>
          <div data-component-id="src/components/sections/mobile/ProductsSectionMobile.tsx:139:10" data-component-path="src/components/sections/mobile/ProductsSectionMobile.tsx" data-component-line="139" data-component-file="ProductsSectionMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-2%20gap-3%20mb-3%20px-4%20items-stretch%22%7D" className="grid grid-cols-2 gap-3 mb-3 px-4 items-stretch">
            {(showAllProducts ? productsToUse : productsToUse.slice(0, 4)).map((product, index) => {
              // Calcular descuento (igual que en desktop)
              const currentPriceNumber = parseFloat(String(product?.priceFinalInc));
              const originalPriceNumber =
                product?.priceOriginalInc !== undefined
                  ? parseFloat(String(product?.priceOriginalInc))
                  : undefined;
              const hasDiscount =
                originalPriceNumber !== undefined &&
                !isNaN(originalPriceNumber) &&
                originalPriceNumber > currentPriceNumber;
              const discountPercent = hasDiscount
                ? Math.round(
                  ((originalPriceNumber - currentPriceNumber) / originalPriceNumber) * 100
                )
                : 0;

              const backgroundStyle = {
                backgroundColor: hexToRgba(generateColor(index) || '#FFDE3B', 0.45), // Mismo que en GlutenFreeSectionMobile
              };
              return (
                <div data-component-id="src/components/sections/mobile/ProductsSectionMobile.tsx:161:16" data-component-path="src/components/sections/mobile/ProductsSectionMobile.tsx" data-component-line="161" data-component-file="ProductsSectionMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22rounded-%5B12px%5D%20p-3%20relative%20shadow-sm%20flex%20flex-col%20w-full%22%7D"
                  key={product.id}
                  className="rounded-[12px] p-3 relative shadow-sm flex flex-col w-full"
                  style={{
                    ...backgroundStyle,
                    minHeight: '245px',
                  }}
                >
                  {/* Badge de descuento */}
                  {hasDiscount && (
                    <div data-component-id="src/components/sections/mobile/ProductsSectionMobile.tsx:171:20" data-component-path="src/components/sections/mobile/ProductsSectionMobile.tsx" data-component-line="171" data-component-file="ProductsSectionMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20top-2%20right-2%20z-10%20bg-%5B%23fcc00a%5D%20px-2%20py-1%20rounded-full%20shadow-md%22%7D" className="absolute top-2 right-2 z-10 bg-[#fcc00a] px-2 py-1 rounded-full shadow-md">
                      <span data-component-id="src/components/sections/mobile/ProductsSectionMobile.tsx:172:22" data-component-path="src/components/sections/mobile/ProductsSectionMobile.tsx" data-component-line="172" data-component-file="ProductsSectionMobile.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-%5B%233d1c1a%5D%20text-%5B10px%5D%20font-bold%22%2C%22textContent%22%3A%22-%20%25%22%7D"
                        className="text-[#3d1c1a] text-[10px] font-bold"
                        style={{
                          fontFamily: "'TT_Norms_Pro-Bold', Helvetica",
                          fontWeight: '700',
                        }}
                      >
                        -{discountPercent}%
                      </span>
                    </div>
                  )}

                  {/* Imagen del producto */}
                  <div data-component-id="src/components/sections/mobile/ProductsSectionMobile.tsx:185:18" data-component-path="src/components/sections/mobile/ProductsSectionMobile.tsx" data-component-line="185" data-component-file="ProductsSectionMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20justify-center%20items-center%20h-28%20mb-3%20flex-shrink-0%20mt-2%20overflow-hidden%22%7D" className="flex justify-center items-center h-28 mb-3 flex-shrink-0 mt-2 overflow-hidden">
                    <Image data-component-id="src/components/sections/mobile/ProductsSectionMobile.tsx:186:20" data-component-path="src/components/sections/mobile/ProductsSectionMobile.tsx" data-component-line="186" data-component-file="ProductsSectionMobile.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22className%22%3A%22object-contain%20cursor-pointer%22%7D"
                      src={product.image}
                      alt={product.title}
                      width={100}
                      height={100}
                      className="object-contain cursor-pointer"
                      onClick={() => router.push(`/${product.slug}`)}
                    />
                  </div>

                  {/* Información del producto */}
                  <div data-component-id="src/components/sections/mobile/ProductsSectionMobile.tsx:197:18" data-component-path="src/components/sections/mobile/ProductsSectionMobile.tsx" data-component-line="197" data-component-file="ProductsSectionMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-left%20flex-1%20flex%20flex-col%20justify-between%22%7D" className="text-left flex-1 flex flex-col justify-between">
                    <div data-component-id="src/components/sections/mobile/ProductsSectionMobile.tsx:198:20" data-component-path="src/components/sections/mobile/ProductsSectionMobile.tsx" data-component-line="198" data-component-file="ProductsSectionMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
                      <h3 data-component-id="src/components/sections/mobile/ProductsSectionMobile.tsx:199:22" data-component-path="src/components/sections/mobile/ProductsSectionMobile.tsx" data-component-line="199" data-component-file="ProductsSectionMobile.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-sm%20leading-4%20text-%5B%233d1c1a%5D%20mb-1%20cursor-pointer%20line-clamp-2%20min-h-8%22%7D"
                        className="text-sm leading-4 text-[#3d1c1a] mb-1 cursor-pointer line-clamp-2 min-h-8"
                        style={{
                          fontFamily: "'TT_Norms_Pro-Bold', Helvetica",
                          fontWeight: '700',
                        }}
                        onClick={() => router.push(`/${product.slug}`)}
                      >
                        {product.title}
                      </h3>
                      {product.brand && (
                        <p data-component-id="src/components/sections/mobile/ProductsSectionMobile.tsx:210:24" data-component-path="src/components/sections/mobile/ProductsSectionMobile.tsx" data-component-line="210" data-component-file="ProductsSectionMobile.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20text-%5B%233d1c1a%5D%20mb-1%22%7D"
                          className="text-xs text-[#3d1c1a] mb-1"
                          style={{
                            fontFamily: "'TT_Norms_Pro-Regular', Helvetica",
                            fontWeight: '400',
                          }}
                        >
                          {product.brand}
                        </p>
                      )}
                      {product.productCombinations && product.productCombinations.length > 0 && (
                        <span data-component-id="src/components/sections/mobile/ProductsSectionMobile.tsx:221:24" data-component-path="src/components/sections/mobile/ProductsSectionMobile.tsx" data-component-line="221" data-component-file="ProductsSectionMobile.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%20text-%5B%233d1c1a%5D%20mb-1%20block%22%7D"
                          className="text-xs text-[#3d1c1a] mb-1 block"
                          style={{
                            fontFamily: "'TT_Norms_Pro-Regular', Helvetica",
                            fontWeight: '400',
                          }}
                        >
                          {product.productCombinations
                            .find((combination) => combination.isDefault)
                            ?.productOptions?.map((option) => option.name)
                            .join(' / ')}
                        </span>
                      )}
                    </div>

                    {/* Precio */}
                    <div data-component-id="src/components/sections/mobile/ProductsSectionMobile.tsx:237:20" data-component-path="src/components/sections/mobile/ProductsSectionMobile.tsx" data-component-line="237" data-component-file="ProductsSectionMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20mt-auto%20pr-10%22%7D" className="flex flex-col mt-auto pr-10">
                        <span data-component-id="src/components/sections/mobile/ProductsSectionMobile.tsx:238:24" data-component-path="src/components/sections/mobile/ProductsSectionMobile.tsx" data-component-line="238" data-component-file="ProductsSectionMobile.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-sm%20text-%5B%233d1c1a%5D%20whitespace-nowrap%22%2C%22textContent%22%3A%22%E2%82%AC%2Fud.%22%7D"
                          className="text-sm text-[#3d1c1a] whitespace-nowrap"
                          style={{
                            fontFamily: "'TT_Norms_Pro-Bold', Helvetica",
                            fontWeight: '700',
                          }}
                        >
                          {formatPrice(currentPriceNumber)} €/ud.
                        </span>
                        {hasDiscount && originalPriceNumber && (
                          <span data-component-id="src/components/sections/mobile/ProductsSectionMobile.tsx:248:26" data-component-path="src/components/sections/mobile/ProductsSectionMobile.tsx" data-component-line="248" data-component-file="ProductsSectionMobile.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-%5B10px%5D%20text-%5B%233d1c1a%5D%2F60%20line-through%22%2C%22textContent%22%3A%22%E2%82%AC%2Fud.%22%7D"
                            className="text-[10px] text-[#3d1c1a]/60 line-through"
                            style={{
                              fontFamily: "'TT_Norms_Pro-Regular', Helvetica",
                              fontWeight: 400,
                            }}
                          >
                            {formatPrice(originalPriceNumber)} €/ud.
                          </span>
                        )}
                    </div>
                  </div>

                  <button data-component-id="src/components/sections/mobile/ProductsSectionMobile.tsx:261:18" data-component-path="src/components/sections/mobile/ProductsSectionMobile.tsx" data-component-line="261" data-component-file="ProductsSectionMobile.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22absolute%20bottom-3%20right-3%20bg-%5B%23FFC700%5D%20rounded-%5B10px%5D%20w-8%20h-8%20flex%20items-center%20justify-center%20hover%3Abg-%5B%23FFC700%5D%2F90%20transition-all%20duration-300%20hover%3Ascale-110%20flex-shrink-0%22%7D"
                    onClick={() => handleAddToCart(product)}
                    className="absolute bottom-3 right-3 bg-[#FFC700] rounded-[10px] w-8 h-8 flex items-center justify-center hover:bg-[#FFC700]/90 transition-all duration-300 hover:scale-110 flex-shrink-0"
                  >
                    <span data-component-id="src/components/sections/mobile/ProductsSectionMobile.tsx:265:20" data-component-path="src/components/sections/mobile/ProductsSectionMobile.tsx" data-component-line="265" data-component-file="ProductsSectionMobile.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-%5B%233d1c1a%5D%20text-lg%20font-bold%20leading-none%22%2C%22textContent%22%3A%22%2B%22%7D" className="text-[#3d1c1a] text-lg font-bold leading-none">+</span>
                  </button>
                </div>
              );
            })}
          </div>

          {/* Botón Ver más */}
          {productsToUse.length > 4 && (
            <div data-component-id="src/components/sections/mobile/ProductsSectionMobile.tsx:274:12" data-component-path="src/components/sections/mobile/ProductsSectionMobile.tsx" data-component-line="274" data-component-file="ProductsSectionMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22px-4%20mb-3%22%7D" className="px-4 mb-3">
              <button data-component-id="src/components/sections/mobile/ProductsSectionMobile.tsx:275:14" data-component-path="src/components/sections/mobile/ProductsSectionMobile.tsx" data-component-line="275" data-component-file="ProductsSectionMobile.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22w-full%20bg-%5B%233d1c1a%5D%20text-white%20py-3%20rounded-%5B12px%5D%20font-tt-norms-bold%20text-base%20hover%3Abg-%5B%233d1c1a%5D%2F90%20transition-all%20duration-300%22%7D"
                onClick={() => setShowAllProducts(!showAllProducts)}
                className="w-full bg-[#3d1c1a] text-white py-3 rounded-[12px] font-tt-norms-bold text-base hover:bg-[#3d1c1a]/90 transition-all duration-300"
              >
                {showAllProducts ? 'Ver menos' : 'Ver más'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ProductsSectionMobile;
